import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, Facebook } from "lucide-react";
import { BUSINESS, NAV_LINKS } from "@/data/site";

const DESKTOP_LINKS = NAV_LINKS.filter((l) =>
  ["/about", "/services", "/senior-living", "/veteran-housing", "/rooms-pricing", "/faq", "/contact"].includes(l.to),
);

const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <>
      <header className="sticky top-0 z-50 backdrop-blur-md bg-ivory/90 border-b border-sand/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 lg:h-24">
          <Link to="/" className="flex items-center group shrink-0" data-testid="header-logo" aria-label="Savannah's House Residential Care Home — Home">
            <img
              src="/logo-360.webp"
              srcSet="/logo-360.webp 360w, /logo-720.webp 720w"
              sizes="(max-width: 1024px) 150px, 190px"
              width="1218"
              height="1129"
              alt="Savannah's House Residential Care Home — Est. Tyler, Texas"
              className="h-16 lg:h-20 w-auto drop-shadow-sm transition-transform duration-300 group-hover:scale-[1.03]"
            />
          </Link>

          <nav aria-label="Primary navigation" className="hidden lg:flex items-center gap-1">
            {DESKTOP_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-medium rounded-full transition-colors ${
                    isActive ? "text-espresso bg-cream" : "text-warmgray hover:text-espresso hover:bg-cream/60"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href={BUSINESS.facebook}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-facebook-link"
              aria-label="Follow Savannah's House on Facebook"
              className="w-10 h-10 rounded-full bg-cream hover:bg-gold hover:text-charcoal text-espresso flex items-center justify-center transition-colors"
            >
              <Facebook size={17} aria-hidden="true" />
            </a>
            <a
              href={BUSINESS.phoneHref}
              data-testid="header-call-button"
              className="flex items-center gap-2 text-sm font-semibold text-espresso hover:text-gold transition-colors"
              aria-label={`Call Savannah's House at ${BUSINESS.phone}`}
            >
              <Phone size={16} aria-hidden="true" />
              {BUSINESS.phone}
            </a>
            <Link
              to="/contact?intent=tour"
              data-testid="header-schedule-tour-button"
              className="bg-espresso hover:bg-charcoal text-ivory text-sm font-semibold px-5 py-2.5 rounded-full shadow-sm hover:shadow-md transition-shadow"
            >
              Schedule a Tour
            </Link>
          </div>

          <button
            type="button"
            data-testid="mobile-menu-button"
            className="lg:hidden inline-flex items-center justify-center w-11 h-11 rounded-full text-espresso hover:bg-cream transition-colors"
            aria-expanded={open}
            aria-controls="mobile-menu"
            aria-label={open ? "Close menu" : "Open menu"}
            onClick={() => setOpen(!open)}
          >
            {open ? <X size={24} aria-hidden="true" /> : <Menu size={24} aria-hidden="true" />}
          </button>
        </div>
        </div>
      </header>

      {open && (
        <div id="mobile-menu" className="lg:hidden fixed inset-0 top-20 bg-ivory z-40 overflow-y-auto" role="dialog" aria-modal="true" aria-label="Site menu">
          <nav aria-label="Mobile navigation" className="px-6 py-8 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `font-serif text-2xl py-3 border-b border-sand/40 transition-colors ${
                    isActive ? "text-gold" : "text-espresso"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="mt-8 flex flex-col gap-3 pb-10">
              <Link
                to="/contact?intent=tour"
                data-testid="mobile-schedule-tour-button"
                className="bg-espresso text-ivory text-center font-semibold px-6 py-4 rounded-full"
              >
                Schedule a Tour
              </Link>
              <a
                href={BUSINESS.phoneHref}
                data-testid="mobile-call-button"
                className="flex items-center justify-center gap-2 border border-espresso text-espresso font-semibold px-6 py-4 rounded-full"
              >
                <Phone size={18} aria-hidden="true" />
                Call {BUSINESS.phone}
              </a>
              <a
                href={BUSINESS.facebook}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-facebook-link"
                className="flex items-center justify-center gap-2 text-espresso font-semibold px-6 py-3 hover:text-gold transition-colors"
              >
                <Facebook size={18} aria-hidden="true" />
                Follow us on Facebook
              </a>
            </div>
          </nav>
        </div>
      )}
    </>
  );
};

export default Header;
